import { DateSchema, type ObjectId, ObjectIdSchema } from '@racemap/sdk/schema/base';
import type { EventBasedStripeProducts } from '@racemap/sdk/schema/billing';
import type { StripeProducts } from '@racemap/sdk/schema/billing/StripeProducts';
import {
  BillableItemTypes,
  type MarketingSchema,
  UserDocumentSchema,
  type User as ZodUser,
} from '@racemap/sdk/schema/user';
import type { Document, Types } from 'mongoose';
import type Stripe from 'stripe';
import { z } from 'zod';

export type { UserDocument } from '@racemap/sdk/schema/user';

export type { DailyLoads } from '@racemap/sdk/schema/billing';

export enum TOSVersions {
  NONE = 'NONE',
  '2019-10' = '2019-10',
}

type Integrations = {
  stripeId?: string;
  // Deep link to the customer in the Stripe dashboard. Built server-side, so it points at the test
  // or live dashboard according to the API key gears-api runs. Admins only.
  stripeCustomerUrl?: string;
  mailchimpSubscribed: boolean;
  mailchimpTags: Array<string>;
  raceResultApiKey: string | null;
  raceResultCustomerId: string | null;
  insidersAccessToken: string | null;
  insidersRefreshToken: string | null;
  insidersExpiresAt: string | null;
};

type UserStatistics = {
  numberCreatedEvents: number;
  numberPaidEvents: number;
};

interface UserSubscriptions {
  app: number;
}

export type User_Legacy = {
  id: string;
  admin?: boolean;
  isBetaUser?: boolean;
  receivedAt?: number;
  updatedAt?: string;
  createdAt?: string;
  isConfirmed?: boolean;
  payments?: {
    lastUsageSync: string;
  };
  name?: string;
  company?: string;
  email?: string;
  phoneNumber?: string;
  country?: string;
  useTimingAPI?: boolean;
  isReseller?: boolean;
  password?: string;
  impersonated?: boolean;
  acceptedTOSVersion?: TOSVersions;
  apiToken?: string;
  stats?: UserStatistics;
  parentId: string | ObjectId | null;
  integrations?: Integrations;
  subscriptions: UserSubscriptions;
  checkout?: {
    address: Stripe.Address;
    taxIds: Array<Stripe.TaxId>;
    company?: string;
    phone?: string;
    shipping: Stripe.Customer.Shipping | null;
    defaultCard: Stripe.Source.Card | null;
  };
  createdByTest?: boolean;
  trackingConsent?: {
    analytics: boolean;
    updatedAt: string;
  };
  // Admins only.
  marketing?: z.input<typeof MarketingSchema>;
};
export type ChildUserLegacy = User_Legacy & {
  parentId: string;
};

export type User = ZodUser;

const UserSnapshotDocumentSchema = z.object({
  _id: ObjectIdSchema,
  userId: ObjectIdSchema,
  createdAt: DateSchema,
  updatedAt: DateSchema,
  user: UserDocumentSchema,
});

export type UserSnapshotDocument = z.infer<typeof UserSnapshotDocumentSchema> &
  Document<Types.ObjectId>;

// editor user added from backend to events
export type Editor = User_Legacy & {
  id: string | ObjectId;
  name: string;
  isReseller: boolean;
};
export type BillableNowCountList = Partial<Record<StripeProducts, number>>;
export type FreeItemCounts = Partial<Record<(typeof EventBasedStripeProducts)[number], number>>;

export type UsageGroupedByTimeKeyAndZone = {
  [timeKey: string]: {
    [BillableItemTypes.DATA_USAGE_ZONE_1]?: number;
    [BillableItemTypes.DATA_USAGE_ZONE_1_V6_SIM]?: number;
    [BillableItemTypes.DATA_USAGE_ZONE_2]?: number;
    [BillableItemTypes.DATA_USAGE_ZONE_2_V6_SIM]?: number;
    [BillableItemTypes.DATA_USAGE_ZONE_3]?: number;
    [BillableItemTypes.DATA_USAGE_ZONE_3_V6_SIM]?: number;
  };
};

export type BillingInvoice = {
  id: string;
  invoicePdf: string;
  invoiceWeb: string;
  createdAt: string;
  paid: boolean;
  number: string;
  amount: number;
};

const SessionSchema = z.object({
  expires: z.string(),
  lastModified: z.string(),
  id: ObjectIdSchema,
  createdAt: z.string(),
  userAgent: z.string(),
  ip: z.string(),
  currentSession: z.boolean().optional(),
});

export type Session = z.infer<typeof SessionSchema>;

const SessionsSchema = z.array(SessionSchema);

export type Sessions = z.infer<typeof SessionsSchema>;

export const ListSessionsResponseSchema = z.object({
  sessions: SessionsSchema,
  currentSessionId: ObjectIdSchema.optional(),
});

export type ListSessionsResponse = z.infer<typeof ListSessionsResponseSchema>;
